import fs from 'node:fs'
import path from 'node:path'

import * as p from '@clack/prompts'
import { spinner } from '@core-engine/utils/spinner.js'
import { guardProjectForDeploy } from '@next-adapter/init/deploy/guard.js'
import { linkRailwayService } from '@next-adapter/init/railway/project.js'
import {
  ensureRailwayAppService,
  readRailwayServiceVariables
} from '@next-adapter/init/railway/resources.js'
import {
  parseRailwayJson,
  railwayOutputTail,
  runRailway
} from '@next-adapter/init/railway/runner.js'
import type {
  RailwayDeployResult,
  RailwayService,
  RailwaySession
} from '@next-adapter/init/railway/types.js'
import pc from 'picocolors'

const DEPLOY_TIMEOUT_MS = 900_000
const ENV_TIMEOUT_MS = 30_000
const ENV_SYNC_SKIP_KEYS = new Set([
  'VERCEL_OIDC_TOKEN',
  'RAILWAY_TOKEN',
  'RAILWAY_API_TOKEN',
  'BETTERSTART_AUTH_URL',
  'NEXT_PUBLIC_BETTERSTART_AUTH_URL'
])

export interface RailwayDeployEnvVar {
  key: string
  value: string
  overwrite: boolean
}

export function buildRailwayProviderOverrides(options: {
  databaseServiceName?: string
  buildCommand?: string
  bucketResourceName?: string
  bucketUrlStyle?: string
}): Record<string, string> {
  const overrides: Record<string, string> = {}
  if (options.databaseServiceName) {
    const resource = options.databaseServiceName
    overrides.DATABASE_URL = `\${{${resource}.DATABASE_URL}}`
    if (options.buildCommand?.trim()) {
      overrides.RAILPACK_BUILD_CMD = `DATABASE_URL='\${{${resource}.DATABASE_PUBLIC_URL}}' ${options.buildCommand}`
    }
  }
  if (options.bucketResourceName) {
    const resource = options.bucketResourceName
    Object.assign(overrides, {
      BETTERSTART_RAILWAY_BUCKET_ENDPOINT: `\${{${resource}.ENDPOINT}}`,
      BETTERSTART_RAILWAY_BUCKET_ACCESS_KEY_ID: `\${{${resource}.ACCESS_KEY_ID}}`,
      BETTERSTART_RAILWAY_BUCKET_SECRET_ACCESS_KEY: `\${{${resource}.SECRET_ACCESS_KEY}}`,
      BETTERSTART_RAILWAY_BUCKET_NAME: `\${{${resource}.BUCKET}}`,
      BETTERSTART_RAILWAY_BUCKET_REGION: `\${{${resource}.REGION}}`
    })
  }
  if (options.bucketUrlStyle) {
    overrides.BETTERSTART_RAILWAY_BUCKET_URL_STYLE = options.bucketUrlStyle
  }
  return overrides
}

function parseEnvFile(filePath: string): Map<string, string> {
  const values = new Map<string, string>()
  if (!fs.existsSync(filePath)) return values

  for (const line of fs.readFileSync(filePath, 'utf-8').split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const separator = trimmed.indexOf('=')
    const key = trimmed.slice(0, separator).trim()
    const value = trimmed
      .slice(separator + 1)
      .replace(/^['"]|['"]$/g, '')
      .trim()
    if (key) values.set(key, value)
  }

  return values
}

function isPlaceholderValue(value: string): boolean {
  return value === 'postgresql://...' || value.startsWith('your_')
}

export function collectRailwayDeployEnvVars(
  cwd: string,
  existingKeys: Set<string>,
  providerOverrides: Record<string, string>
): RailwayDeployEnvVar[] {
  const variables: RailwayDeployEnvVar[] = []
  const local = parseEnvFile(path.join(cwd, '.env.local'))

  for (const [key, value] of local) {
    if (ENV_SYNC_SKIP_KEYS.has(key) || !value || isPlaceholderValue(value)) continue
    if (key in providerOverrides) continue
    if (existingKeys.has(key)) continue
    variables.push({ key, value, overwrite: false })
  }

  for (const [key, value] of Object.entries(providerOverrides)) {
    variables.push({ key, value, overwrite: true })
  }

  return variables
}

async function setRailwayVariable(
  session: RailwaySession,
  cwd: string,
  service: RailwayService,
  variable: RailwayDeployEnvVar
): Promise<boolean> {
  const result = await runRailway(
    session.runner,
    [
      'variable',
      'set',
      variable.key,
      '--stdin',
      '--skip-deploys',
      '--service',
      service.name,
      '--environment',
      session.project.environmentName,
      '--json'
    ],
    {
      cwd,
      mode: 'capture',
      timeoutMs: ENV_TIMEOUT_MS,
      env: session.env,
      stdinInput: variable.value
    }
  )
  return result.success
}

export async function syncRailwayServiceEnv(
  session: RailwaySession,
  cwd: string,
  service: RailwayService,
  providerOverrides: Record<string, string>
): Promise<{ synced: string[]; failed: string[] }> {
  const existing = await readRailwayServiceVariables(session, cwd, service.name)
  const variables = collectRailwayDeployEnvVars(
    cwd,
    new Set(Object.keys(existing)),
    providerOverrides
  )
  const synced: string[] = []
  const failed: string[] = []

  for (const variable of variables) {
    if (await setRailwayVariable(session, cwd, service, variable)) synced.push(variable.key)
    else failed.push(variable.key)
  }

  return { synced, failed }
}

function parseDomainUrl(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const normalized = value.startsWith('http') ? value : `https://${value}`
    try {
      return new URL(normalized).toString().replace(/\/$/, '')
    } catch {
      return undefined
    }
  }

  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  return parseDomainUrl(record.domain)
}

function firstDomainUrl(payload: unknown): string | undefined {
  const direct = parseDomainUrl(payload)
  if (direct) return direct
  if (!payload || typeof payload !== 'object') return undefined
  const domains = (payload as Record<string, unknown>).domains
  return Array.isArray(domains)
    ? domains.map(parseDomainUrl).find((domain): domain is string => Boolean(domain))
    : undefined
}

function parseDomainList(payload: unknown): string[] | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined
  const domains = (payload as Record<string, unknown>).domains
  if (!Array.isArray(domains)) return undefined

  const parsed = domains.map(parseDomainUrl)
  return parsed.every((domain): domain is string => Boolean(domain)) ? parsed : undefined
}

async function ensureRailwayDomain(
  session: RailwaySession,
  cwd: string,
  service: RailwayService
): Promise<string | undefined> {
  const common = [
    '--service',
    service.name,
    '--environment',
    session.project.environmentName,
    '--json'
  ]
  const list = await runRailway(session.runner, ['domain', 'list', ...common], {
    cwd,
    mode: 'capture',
    env: session.env
  })
  if (!list.success) {
    throw new Error(
      railwayOutputTail(`${list.stdout}\n${list.stderr}`) ??
        `Could not list Railway domains for ${service.name}.`
    )
  }

  const domains = parseDomainList(parseRailwayJson<unknown>(list.stdout))
  if (!domains) {
    throw new Error(`Railway returned an invalid domain list response for ${service.name}.`)
  }
  const existing = domains[0]
  if (existing) return existing

  const create = await runRailway(session.runner, ['domain', ...common], {
    cwd,
    mode: 'capture',
    env: session.env
  })
  return create.success ? firstDomainUrl(parseRailwayJson<unknown>(create.stdout)) : undefined
}

function deploymentLine(line: string): string | undefined {
  let message = line
  try {
    const parsed = JSON.parse(line) as Record<string, unknown>
    const jsonMessage = parsed.message ?? parsed.status
    if (typeof jsonMessage === 'string') message = jsonMessage
  } catch {
    // Non-JSON Railway output is already suitable for the spinner.
  }
  // Railway's JSON events carry raw build-log indentation and tabs. A tab
  // renders wider than the one column the spinner's width math counts, which
  // wraps clack's animated dots onto the next terminal row — collapse all
  // whitespace runs so the spinner line measures the way it renders.
  message = message.replace(/\s+/g, ' ').trim()
  return message === '' || /^\.+$/.test(message) ? undefined : message.slice(0, 140)
}

async function readFailedDeploymentLogs(
  session: RailwaySession,
  cwd: string,
  service: RailwayService
): Promise<string | undefined> {
  const logs = await runRailway(
    session.runner,
    [
      'logs',
      '--service',
      service.name,
      '--environment',
      session.project.environmentName,
      '--lines',
      '100',
      '--json'
    ],
    { cwd, mode: 'capture', timeoutMs: 60_000, env: session.env }
  )
  return railwayOutputTail(`${logs.stdout}\n${logs.stderr}`)
}

export async function runRailwayDeployFlow(options: {
  session: RailwaySession
  cwd: string
  service?: string
  providerOverrides?: Record<string, string>
}): Promise<RailwayDeployResult> {
  try {
    const prepSpinner = spinner()
    prepSpinner.start('Connecting the Railway web service')
    let service: RailwayService
    let url: string | undefined
    try {
      service = await ensureRailwayAppService(
        options.session,
        options.cwd,
        options.service?.trim() || 'web'
      )
      if ((service.replicaCount ?? 1) > 1) {
        throw new Error(
          `Railway service ${service.name} has ${service.replicaCount} replicas. BetterStart Railway v1 supports one replica because Next.js cache invalidation is process-local.`
        )
      }
      await linkRailwayService(
        options.session.runner,
        options.cwd,
        options.session.project,
        service,
        options.session.env
      )

      prepSpinner.message('Setting up the Railway domain')
      url = await ensureRailwayDomain(options.session, options.cwd, service)
    } finally {
      prepSpinner.clear()
    }
    if (!url) {
      throw new Error('Railway could not create a public domain required for Better Auth.')
    }
    const providerOverrides = {
      ...options.providerOverrides,
      BETTERSTART_AUTH_URL: url,
      NEXT_PUBLIC_BETTERSTART_AUTH_URL: url
    }

    const envSpinner = spinner()
    envSpinner.start('Syncing environment variables to Railway')
    const sync = await syncRailwayServiceEnv(
      options.session,
      options.cwd,
      service,
      providerOverrides
    )
    envSpinner.clear()
    for (const key of sync.failed) {
      p.log.warn(`Could not set ${pc.cyan(key)} on Railway.`)
    }
    if (sync.failed.length > 0) {
      return {
        ok: false,
        service,
        syncedEnvKeys: sync.synced,
        detail: `Railway environment sync failed for: ${sync.failed.join(', ')}.`
      }
    }

    const guardSpinner = spinner()
    guardSpinner.start('Preparing the project for deploy')
    const packageGuard = await guardProjectForDeploy(options.cwd)
    guardSpinner.clear()
    if (packageGuard.lockfile === 'failed') {
      p.log.warn(
        'Could not refresh pnpm-lock.yaml after removing betterstart-cli — the remote install may fail.'
      )
    }
    for (const dependency of packageGuard.localSpecDeps) {
      p.log.warn(
        `Dependency ${pc.cyan(dependency)} uses a local spec that cannot install on Railway.`
      )
    }

    const deploySpinner = spinner()
    deploySpinner.start('Deploying to Railway')
    let deploy: Awaited<ReturnType<typeof runRailway>>
    try {
      deploy = await runRailway(
        options.session.runner,
        [
          'up',
          '.',
          '--path-as-root',
          '--service',
          service.name,
          '--environment',
          options.session.project.environmentName,
          '--json'
        ],
        {
          cwd: options.cwd,
          mode: 'capture',
          timeoutMs: DEPLOY_TIMEOUT_MS,
          env: options.session.env,
          onOutputLine: (line) => {
            const message = deploymentLine(line)
            if (message) deploySpinner.message(message)
          }
        }
      )
    } finally {
      packageGuard.restore()
    }

    if (!deploy.success) {
      deploySpinner.stop(`${pc.yellow('▲')} Railway deploy did not complete`)
      const logs = await readFailedDeploymentLogs(options.session, options.cwd, service)
      return {
        ok: false,
        service,
        syncedEnvKeys: sync.synced,
        detail:
          logs ?? railwayOutputTail(`${deploy.stdout}\n${deploy.stderr}`) ?? deploy.errorMessage
      }
    }

    deploySpinner.stop(url ? `Deployed ${pc.cyan(url)}` : 'Deployed to Railway')
    return { ok: true, url, service, syncedEnvKeys: sync.synced }
  } catch (error) {
    return { ok: false, detail: error instanceof Error ? error.message : String(error) }
  }
}
